import type { ReactNode } from 'react'

import { useDestinos } from '../../api/destinos'
import { Aviso, Selector, claseDeEntrada, clases } from '../../componentes/ui'
import type { Campo } from '../esquema'
import { useFormulario, useValor } from '../Formulario'

export { claseDeEntrada }

/**
 * Los controles simples: uno por tipo de campo de Payload. Todos leen y escriben por
 * su ruta en el formulario, y todos enseñan la etiqueta, la descripción y el error
 * del mismo modo: el error va **junto al campo** y anunciado (`aria-describedby`), no
 * en una lista al final.
 */
export const Envoltura = ({
  campo,
  ruta,
  error,
  children,
  enLinea = false,
}: {
  campo: Campo
  children: ReactNode
  enLinea?: boolean
  error?: string
  ruta: string
}) => {
  const idError = `${ruta}-error`
  const idAyuda = `${ruta}-ayuda`
  const etiqueta = (
    <label className={clases('text-[13px] font-medium text-tinta', enLinea && 'cursor-pointer')} htmlFor={ruta}>
      {campo.etiqueta}
      {campo.obligatorio && !enLinea && (
        <span aria-hidden className="ml-0.5 text-naranja">
          *
        </span>
      )}
    </label>
  )
  return (
    <div className={clases('flex', enLinea ? 'flex-row flex-wrap items-center gap-x-2.5 gap-y-1' : 'flex-col gap-1.5')}>
      {!enLinea && etiqueta}
      {children}
      {enLinea && etiqueta}
      {campo.descripcion && !error && (
        <p className={clases('text-xs text-tenue', enLinea && 'basis-full pl-6.5')} id={idAyuda}>
          {campo.descripcion}
        </p>
      )}
      {error && (
        <p className={clases('text-xs text-error', enLinea && 'basis-full pl-6.5')} id={idError} role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

const describe = (ruta: string, campo: Campo, error?: string) => (error ? `${ruta}-error` : campo.descripcion ? `${ruta}-ayuda` : undefined)

export const Texto = ({ campo, ruta, soloLectura }: { campo: Campo; ruta: string; soloLectura: boolean }) => {
  const { valor, cambiar, error } = useValor<string>(ruta)
  const { sitio } = useFormulario()
  // Un campo que ofrece las direcciones publicadas de la web (el destino de una redirección):
  // se eligen de la lista al escribir, y se puede seguir escribiendo otra.
  const { data: destinos } = useDestinos(campo.sugerencias === 'rutas' && !soloLectura ? sitio : null)
  const lista = destinos && destinos.length > 0 ? `${ruta}-sugerencias` : undefined
  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <input
        aria-describedby={describe(ruta, campo, error)}
        aria-invalid={Boolean(error)}
        className={claseDeEntrada(error)}
        disabled={soloLectura}
        id={ruta}
        list={lista}
        maxLength={campo.maxLongitud}
        onChange={(e) => cambiar(e.target.value)}
        placeholder={lista ? 'Escribe para elegir una página de tu web' : campo.placeholder}
        type={campo.tipo === 'email' ? 'email' : campo.tipo === 'password' ? 'password' : 'text'}
        value={valor ?? ''}
      />
      {lista && (
        <datalist id={lista}>
          {destinos!.map((d) => (
            <option key={d.ruta} value={d.ruta}>
              {d.titulo}
            </option>
          ))}
        </datalist>
      )}
    </Envoltura>
  )
}

export const AreaDeTexto = ({ campo, ruta, soloLectura }: { campo: Campo; ruta: string; soloLectura: boolean }) => {
  const { valor, cambiar, error } = useValor<string>(ruta)
  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <textarea
        aria-describedby={describe(ruta, campo, error)}
        aria-invalid={Boolean(error)}
        className={clases(claseDeEntrada(error), 'h-auto min-h-24 resize-y py-2')}
        disabled={soloLectura}
        id={ruta}
        maxLength={campo.maxLongitud}
        onChange={(e) => cambiar(e.target.value)}
        placeholder={campo.placeholder}
        value={valor ?? ''}
      />
    </Envoltura>
  )
}

export const Numero = ({ campo, ruta, soloLectura }: { campo: Campo; ruta: string; soloLectura: boolean }) => {
  const { valor, cambiar, error } = useValor<number | null>(ruta)
  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <input
        aria-describedby={describe(ruta, campo, error)}
        aria-invalid={Boolean(error)}
        className={clases(claseDeEntrada(error), 'max-w-xs tabular-nums')}
        disabled={soloLectura}
        id={ruta}
        max={campo.max}
        min={campo.min}
        onChange={(e) => cambiar(e.target.value === '' ? null : Number(e.target.value))}
        placeholder={campo.placeholder}
        step="any"
        type="number"
        value={valor ?? ''}
      />
    </Envoltura>
  )
}

export const Casilla = ({ campo, ruta, soloLectura }: { campo: Campo; ruta: string; soloLectura: boolean }) => {
  const { valor, cambiar, error } = useValor<boolean>(ruta)
  return (
    <Envoltura campo={campo} enLinea error={error} ruta={ruta}>
      <input
        checked={Boolean(valor)}
        className="size-4 shrink-0 rounded border-linea-fuerte accent-violeta"
        disabled={soloLectura}
        id={ruta}
        onChange={(e) => cambiar(e.target.checked)}
        type="checkbox"
      />
    </Envoltura>
  )
}

const aFechaLocal = (iso: string | undefined): string => {
  if (!iso) return ''
  const fecha = new Date(iso)
  if (Number.isNaN(fecha.getTime())) return ''
  const p = (n: number) => String(n).padStart(2, '0')
  return `${fecha.getFullYear()}-${p(fecha.getMonth() + 1)}-${p(fecha.getDate())}T${p(fecha.getHours())}:${p(fecha.getMinutes())}`
}

export const Fecha = ({ campo, ruta, soloLectura }: { campo: Campo; ruta: string; soloLectura: boolean }) => {
  const { valor, cambiar, error } = useValor<string | null>(ruta)
  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <input
        aria-describedby={describe(ruta, campo, error)}
        aria-invalid={Boolean(error)}
        className={clases(claseDeEntrada(error), 'max-w-xs')}
        disabled={soloLectura}
        id={ruta}
        onChange={(e) => cambiar(e.target.value ? new Date(e.target.value).toISOString() : null)}
        type="datetime-local"
        value={aFechaLocal(valor ?? undefined)}
      />
    </Envoltura>
  )
}

/** Un chip elegible: para selects múltiples y radios, que así se ven de un vistazo. */
export const Chip = ({ activo, children, disabled, onClick, htmlFor }: { activo: boolean; children: ReactNode; disabled?: boolean; htmlFor?: string; onClick?: () => void }) => {
  const clase = clases(
    'inline-flex h-8 cursor-pointer items-center rounded-boton border px-3 text-[13px] transition-colors',
    activo ? 'border-violeta-borde bg-violeta-suave font-medium text-violeta' : 'border-linea bg-papel text-tinta-2 hover:border-linea-fuerte hover:bg-papel-2',
    disabled && 'cursor-not-allowed opacity-60',
  )
  return htmlFor ? (
    <label className={clase} htmlFor={htmlFor}>
      {children}
    </label>
  ) : (
    <button aria-pressed={activo} className={clase} disabled={disabled} onClick={onClick} type="button">
      {children}
    </button>
  )
}

export const Seleccion = ({ campo, ruta, soloLectura }: { campo: Campo; ruta: string; soloLectura: boolean }) => {
  const { valor, cambiar, error } = useValor<string | string[] | null>(ruta)
  const opciones = campo.opciones ?? []
  if (campo.varios) {
    const elegidas = Array.isArray(valor) ? valor : []
    return (
      <Envoltura campo={campo} error={error} ruta={ruta}>
        <div className="flex flex-wrap gap-1.5" id={ruta}>
          {opciones.map((o) => {
            const activa = elegidas.includes(o.valor)
            return (
              <Chip activo={activa} disabled={soloLectura} key={o.valor} onClick={() => cambiar(activa ? elegidas.filter((v) => v !== o.valor) : [...elegidas, o.valor])}>
                {o.etiqueta}
              </Chip>
            )
          })}
        </div>
      </Envoltura>
    )
  }
  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <Selector
        aria-describedby={describe(ruta, campo, error)}
        aria-invalid={Boolean(error)}
        className={clases(error && 'border-error')}
        disabled={soloLectura}
        id={ruta}
        onChange={(e) => cambiar(e.target.value === '' ? null : e.target.value)}
        value={typeof valor === 'string' ? valor : ''}
      >
        {!campo.obligatorio && <option value="">—</option>}
        {opciones.map((o) => (
          <option key={o.valor} value={o.valor}>
            {o.etiqueta}
          </option>
        ))}
      </Selector>
      {/* Si la opción elegida dice qué significa (un rol, por ejemplo), se lee aquí mismo. */}
      {(() => {
        const elegida = opciones.find((o) => o.valor === valor)
        return elegida?.descripcion ? <p className="text-xs text-tinta-2">{elegida.descripcion}</p> : null
      })()}
    </Envoltura>
  )
}

export const Radio = ({ campo, ruta, soloLectura }: { campo: Campo; ruta: string; soloLectura: boolean }) => {
  const { valor, cambiar, error } = useValor<string | null>(ruta)
  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <div className="flex flex-wrap gap-1.5" id={ruta} role="radiogroup">
        {(campo.opciones ?? []).map((o) => {
          const activa = valor === o.valor
          const id = `${ruta}-${o.valor}`
          return (
            <span key={o.valor}>
              <input checked={activa} className="sr-only" disabled={soloLectura} id={id} name={ruta} onChange={() => cambiar(o.valor)} type="radio" value={o.valor} />
              <Chip activo={activa} disabled={soloLectura} htmlFor={id}>
                {o.etiqueta}
              </Chip>
            </span>
          )
        })}
      </div>
    </Envoltura>
  )
}

/** JSON y código: un área monoespaciada que solo escribe cuando lo que hay dentro es JSON válido. */
export const Json = ({ campo, ruta, soloLectura }: { campo: Campo; ruta: string; soloLectura: boolean }) => {
  const { valor, cambiar, error } = useValor<unknown>(ruta)
  // Lo que escribe el servidor para enseñarlo —una lista de frases, como los avisos del
  // robots.txt— se lee como avisos, no como JSON.
  if (campo.soloLectura && Array.isArray(valor) && valor.length > 0 && valor.every((v) => typeof v === 'string')) {
    return (
      <div className="flex flex-col gap-1.5">
        <p className="text-[13px] font-medium text-tinta">{campo.etiqueta}</p>
        {(valor as string[]).map((frase) => (
          <Aviso key={frase} tono="aviso">
            {frase}
          </Aviso>
        ))}
      </div>
    )
  }
  const texto = valor === undefined || valor === null ? '' : typeof valor === 'string' ? valor : JSON.stringify(valor, null, 2)
  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <textarea
        className={clases(claseDeEntrada(error), 'h-auto min-h-32 py-2 font-mono text-[13px]')}
        defaultValue={texto}
        disabled={soloLectura}
        id={ruta}
        onBlur={(e) => {
          const t = e.target.value.trim()
          if (!t) return cambiar(null)
          try {
            cambiar(campo.tipo === 'json' ? JSON.parse(t) : t)
          } catch {
            // Se deja tal cual: el error lo dirá el servidor al guardar, con su mensaje.
            cambiar(t)
          }
        }}
        spellCheck={false}
      />
    </Envoltura>
  )
}
